import { Controller } from "@hotwired/stimulus"

// The upload forms' helper, for one photo (a vehicle's side) or several (an
// issue's). The forms work without it -- the file input sits behind a
// <label> styled as a button, and a label opens its input's picker natively
// -- so everything here is an improvement, never a requirement:
//
//   • a preview of each chosen photo, and its name, before anything is sent;
//   • an over-size file, or one photo too many, refused on the phone before
//     it spends the data sending it (the model's validation is still the gate);
//   • the submit button disabled until there is something to submit, where
//     a photo is the whole point of the form (the `submit` target).
//
// Several photos: a phone's Take Photo gives ONE photo per pick, and a new
// pick replaces whatever the input held -- so the photos chosen so far live
// here, each pick adds to them (camera shots and library photos alike), and
// the list is written back into the input (a DataTransfer) for the form to
// send. Each preview has a Remove. Without JavaScript, one pick is what is
// sent.
//
// Object URLs are revoked whenever the previews are redrawn and when the form
// leaves the page (Turbo keeps the document alive across visits, so a URL
// that is never revoked holds the file's memory until the tab closes).
//
// Markup contract:
//   <form data-controller="photo-picker" data-photo-picker-max-bytes-value="15728640"
//         data-photo-picker-max-count-value="10">                   (max-count: the room left, several only)
//     <input type="file" [multiple] data-photo-picker-target="input" data-action="photo-picker#changed">
//     <img data-photo-picker-target="preview" hidden>               (one photo)
//     <ul data-photo-picker-target="previews"></ul>                 (several)
//     <p data-photo-picker-target="status"></p>
//     <input type="submit" data-photo-picker-target="submit">      (optional)
export default class extends Controller {
  static targets = ["input", "preview", "previews", "status", "submit"]
  static values = { maxBytes: Number, maxCount: Number }

  connect() {
    this.urls = []
    this.files = [...this.inputTarget.files]
    // Disabled here, not in the markup: without JavaScript the button has to
    // work, and the server answers a missing photo with a sentence.
    this.toggleSubmit(this.files.length > 0)
  }

  disconnect() {
    this.revoke()
  }

  changed() {
    const picked = [...this.inputTarget.files]
    const tooBig = picked.filter((file) => this.maxBytesValue && file.size > this.maxBytesValue)
    const refusals = tooBig.map((file) =>
      `${file.name} is ${this.megabytes(file.size)} MB; photos must be ${this.megabytes(this.maxBytesValue)} MB or smaller.`)

    let files = picked.filter((file) => !tooBig.includes(file))
    if (this.inputTarget.multiple) files = this.files.concat(files)

    // The room left, which is not the owner's limit on an edit ("3 more"),
    // so the sentence says what can be added here, not what the limit is.
    if (this.maxCountValue && files.length > this.maxCountValue) {
      const room = this.maxCountValue
      const left = files.length - room
      refusals.push(`Only ${room === 1 ? "one photo" : `${room} photos`} can be added here; ` +
        `${left === 1 ? "one was" : `${left} were`} left out.`)
      files = files.slice(0, room)
    }

    this.files = files
    this.writeBack()
    this.render(refusals)
  }

  remove(event) {
    this.files.splice(Number(event.params.index), 1)
    this.writeBack()
    this.render([])
  }

  writeBack() {
    const transfer = new DataTransfer()
    this.files.forEach((file) => transfer.items.add(file))
    this.inputTarget.files = transfer.files
  }

  render(refusals) {
    this.revoke()

    if (this.hasPreviewTarget) this.showOne(this.files[0])
    if (this.hasPreviewsTarget) this.showEach()

    const chosen = this.inputTarget.multiple && this.files.length > 0
      ? `${this.files.length === 1 ? "1 photo" : `${this.files.length} photos`}: ${this.files.map((file) => file.name).join(", ")}`
      : this.files[0]?.name ?? ""
    this.statusTarget.textContent = [...refusals, chosen].filter(Boolean).join(" ")
    this.toggleSubmit(this.files.length > 0)
  }

  showOne(file) {
    if (!file) {
      this.previewTarget.hidden = true
      this.previewTarget.removeAttribute("src")
      return
    }
    // A HEIC the browser cannot draw (anything but Safari) keeps its name and
    // loses only the preview; the server converts it.
    this.previewTarget.onerror = () => { this.previewTarget.hidden = true }
    this.previewTarget.src = this.urlFor(file)
    this.previewTarget.hidden = false
  }

  showEach() {
    this.previewsTarget.replaceChildren(...this.files.map((file, index) => {
      const item = document.createElement("li")
      item.className = "relative"

      const image = document.createElement("img")
      image.alt = file.name
      image.className = "aspect-square w-full rounded-lg object-cover bg-gray-100 dark:bg-gray-800"
      image.onerror = () => { image.removeAttribute("src") }
      image.src = this.urlFor(file)

      const remove = document.createElement("button")
      remove.type = "button"
      remove.textContent = "Remove"
      remove.setAttribute("aria-label", `Remove ${file.name}`)
      remove.dataset.action = "photo-picker#remove"
      remove.dataset.photoPickerIndexParam = index
      remove.className = "mt-1 w-full min-h-11 lg:min-h-9 rounded-lg border border-red-200 dark:border-red-900 " +
        "text-sm font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 cursor-pointer"

      item.append(image, remove)
      return item
    }))
  }

  urlFor(file) {
    const url = URL.createObjectURL(file)
    this.urls.push(url)
    return url
  }

  toggleSubmit(enabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = !enabled
  }

  revoke() {
    this.urls.forEach((url) => URL.revokeObjectURL(url))
    this.urls = []
  }

  // One decimal, so a 15.3 MB photo is not refused as "15 MB; photos must be
  // 15 MB or smaller". Number() drops a trailing ".0": 16, not 16.0.
  megabytes(bytes) {
    return Number((bytes / 1024 / 1024).toFixed(1))
  }
}
